import { compose } from '@descope-ui/common/utils';
import {
  limitAbbreviation,
  getComponentName,
  injectStyle,
} from '@descope-ui/common/components-helpers';
import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
  createDynamicDataMixin,
} from '@descope-ui/common/components-mixins';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { IconClass } from '@descope-ui/descope-icon/class';
import { TextClass } from '@descope-ui/descope-text/class';
import { ListClass } from '@descope-ui/descope-list/class';
import { ListItemClass } from '@descope-ui/descope-list-item/class';

export const componentName = getComponentName('outbound-apps');

const itemRenderer = (
  { name, description, logo, appId, isConnected },
  _,
  ref,
) => {
  const action = isConnected ? 'disconnect' : 'connect';
  return `
  <descope-list-item>
    <descope-avatar
      ${logo ? `img="${logo}"` : ''}
      ${name ? `display-name="${name}" abbr=${limitAbbreviation(name)}` : ''}
      size=${ref.size}
      ${logo ? 'st-avatar-background-color="none"' : ''}
      class="app-logo"
    ></descope-avatar>

      <div class="content">
        <descope-text
          class="app-title"
          variant="subtitle1"
          mode="primary"
        >${name}</descope-text>
      ${
        description
          ? `
          <descope-text
            class="app-description"
            variant="body2"
            mode="primary"
          >${description}</descope-text>
      `
          : ''
      }
    </div>
    <div class="controls">
      <descope-button variant="link" mode="primary" size=${ref.size} data-action="${action}" data-outbound-app-id="${appId}" data-id="${action}">
        ${isConnected ? ref.disconnectButtonLabel : ref.connectButtonLabel}
      </descope-button>
    </div>
  </descope-list-item>
`;
};

const BaseClass = createBaseClass({
  componentName,
  baseSelector: 'descope-list',
});

class RawOutboundAppsClass extends BaseClass {
  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
      <descope-list>
        <slot name="empty-state" slot="empty-state"></slot>
      </descope-list>
    `;

    this.appsList = this.shadowRoot.querySelector('descope-list');

    injectStyle(
      `
      :host {
        width: 100%;
      }
      .controls {
        display: flex;
        min-width: 8em;
        justify-content: end;
      }
      .content {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
      }
      .app-title,
      .app-description {
        display: flex;
        direction: inherit;
      }
      descope-list {
        display: block;
      }
      `,
      this,
    );
  }

  init() {
    super.init?.();

    this.appsList.itemRenderer = itemRenderer;

    this.appsList.addEventListener('click', this.onAppsListClick.bind(this));
  }

  get readonly() {
    return this.getAttribute('readonly') === 'true';
  }

  onAppsListClick(e) {
    if (!this.readonly) {
      const id = e.srcElement.getAttribute('data-outbound-app-id');
      const action = e.srcElement.getAttribute('data-action');
      if (id && action) {
        this.dispatchEvent(
          new CustomEvent(`${action}-clicked`, {
            bubbles: true,
            detail: { id, action },
          }),
        );
      }
    }
  }

  get size() {
    return this.getAttribute('size') || 'sm';
  }

  get connectButtonLabel() {
    return this.getAttribute('connect-button-label') || 'Connect';
  }

  get disconnectButtonLabel() {
    return this.getAttribute('disconnect-button-label') || 'Disconnect';
  }
}

const { host } = {
  host: { selector: () => ':host' },
};

export const OutboundAppsClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { ...host, property: 'width' },
      minHeight: { selector: () => ':host' },
      hostDirection: [
        { ...host, property: 'direction' },
        {
          selector: 'descope-list',
          property: 'direction',
        },
        {
          selector: 'descope-list-item',
          property: 'direction',
        },
      ],
      iconColor: {
        selector: () => ' descope-icon',
        property: IconClass.cssVarList.fill,
      },
      errorIconColor: {
        selector: () => ' descope-icon.error-icon',
        property: IconClass.cssVarList.fill,
      },
      fontSize: {
        selector: TextClass.componentName,
        property: TextClass.cssVarList.fontSize,
      },
      appLogoGap: {
        selector: () => ' .app-logo',
        property: 'margin-inline-end',
      },
      contentGap: {
        selector: () => ' .content',
        property: 'gap',
      },
      itemsTextAlign: {
        selector: TextClass.componentName,
        property: TextClass.cssVarList.textAlign,
      },
      itemCursor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.cursor,
      },
      itemOutline: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.outline,
      },
      itemBorderColor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.borderColor,
      },
      itemBackgroundColor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.backgroundColor,
      },
      listBorderWidth: {
        selector: () => ListClass.componentName,
        property: ListClass.cssVarList.borderWidth,
      },
      listBoxShadow: {
        selector: () => ListClass.componentName,
        property: ListClass.cssVarList.boxShadow,
      },
      listPadding: [
        {
          selector: () => ListClass.componentName,
          property: ListClass.cssVarList.verticalPadding,
        },
        {
          selector: () => ListClass.componentName,
          property: ListClass.cssVarList.horizontalPadding,
        },
      ],
    },
  }),
  draggableMixin,
  createDynamicDataMixin({
    itemRenderer,
    rerenderAttrsList: [
      'size',
      'connect-button-label',
      'disconnect-button-label',
    ],
  }),
  componentNameValidationMixin,
)(RawOutboundAppsClass);
